/**
 * @fileoverview Tech Portal Layout - Premium Washed Charcoal Aesthetic (Mobile-First)
 *
 * Mobile-first layout for field technicians with premium design matching main portals.
 * Features bottom navigation optimized for touch, minimal chrome, and offline indicators.
 *
 * DESIGN SYSTEM:
 * - Background: #292929 (main), #2d2d2d, #2c2c2c (surfaces)
 * - Borders: white/[0.08], white/[0.05]
 * - Text: gray-100 (headings), gray-400 (body), gray-500 (labels)
 * - Accents: Lime #84CC16, Teal #14B8A6 at 70-80% opacity
 * - Sharp corners, premium shadows, mobile-optimized spacing
 *
 * Mobile priorities:
 * - Large touch targets (48px minimum)
 * - Easy one-handed operation
 * - High contrast (readable in sunlight)
 * - Offline status awareness
 */

import { Link, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  ClipboardList,
  Camera,
  User,
  Wifi,
  WifiOff,
  Phone,
  MapPin,
  RefreshCw,
  Loader2,
} from "lucide-react";
import mynaLogo from "@/assets/myna-logo.svg";
import { useOfflineSync } from "@/hooks/use-offline-sync";
import { useCurrentTenant } from "@/hooks/use-tenant";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { toast } from "sonner";

const navItems = [
  { title: "Home", href: "/app/tech", icon: Home },
  { title: "Jobs", href: "/app/tech/jobs", icon: ClipboardList },
  { title: "Survey", href: "/app/tech/survey", icon: Camera },
  { title: "Profile", href: "/app/tech/profile", icon: User },
];

export function TechLayout() {
  const location = useLocation();
  const { isOnline, isSyncing, pendingCount, syncNow } = useOfflineSync();
  const { data: tenant } = useCurrentTenant();

  // Derive dispatch phone from tenant config or contact phone
  const dispatchPhone = (tenant?.config?.dispatch_phone as string) || tenant?.contact_phone || null;

  const isActive = (href: string) => {
    if (href === "/app/tech") {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#292929] text-white flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-lime-500 focus:text-[#292929] focus:font-semibold focus:rounded-md">
        Skip to main content
      </a>
      {/* Gradient wash background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2e2e2e] via-[#292929] to-[#282828]" />
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px]"
          style={{
            background: "radial-gradient(circle, rgba(20,184,166,0.015) 0%, transparent 70%)",
            filter: "blur(120px)",
          }}
        />
      </div>

      {/* Offline Banner */}
      {!isOnline && (
        <div className="relative z-20 bg-yellow-500 text-black text-center py-3 text-sm font-bold flex items-center justify-center gap-2 shadow-lg">
          <WifiOff className="w-5 h-5" strokeWidth={2.5} />
          <span>
            You're offline.
            {pendingCount > 0 && ` ${pendingCount} pending change${pendingCount > 1 ? 's' : ''}.`}
          </span>
        </div>
      )}

      {/* Syncing Banner */}
      {isSyncing && (
        <div className="relative z-20 bg-teal-500 text-white text-center py-3 text-sm font-bold flex items-center justify-center gap-2 shadow-lg">
          <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
          <span>Syncing pending changes...</span>
        </div>
      )}

      {/* Pending Operations Banner (when online but have pending ops) */}
      {isOnline && !isSyncing && pendingCount > 0 && (
        <button
          onClick={syncNow}
          className="relative z-20 w-full bg-lime-500 text-[#1f1f1f] text-center py-3 text-sm font-black flex items-center justify-center gap-2 hover:bg-lime-400 transition-all shadow-lg active:scale-[0.98]"
        >
          <RefreshCw className="w-5 h-5" strokeWidth={2.5} />
          <span>Tap to sync {pendingCount} pending change{pendingCount > 1 ? 's' : ''}</span>
        </button>
      )}

      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* Top Header */}
      <header className="relative z-10 h-16 border-b border-white/[0.08] bg-[#2d2d2d] flex items-center justify-between px-4 sticky top-0 shadow-xl">
        <Link to="/app/tech" className="flex items-center gap-2">
          <img src={mynaLogo} alt="MYNA AI" className="h-20 w-auto opacity-90" />
          <div className="px-2 py-0.5 bg-teal-500/10 border border-teal-500/30">
            <span className="text-[10px] text-teal-400/80 font-semibold tracking-wider uppercase">
              Tech Portal
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {/* Connection Status */}
          <div className={`flex items-center gap-1 px-2 py-1 ${isOnline ? 'text-lime-400' : 'text-yellow-500'}`}>
            {isOnline ? <Wifi className="w-4 h-4" strokeWidth={2} /> : <WifiOff className="w-4 h-4" strokeWidth={2} />}
          </div>

          {/* Quick Actions */}
          <button
            onClick={() => {
              if (dispatchPhone) {
                window.location.href = `tel:${dispatchPhone}`;
              } else {
                toast.error("No dispatch number configured", {
                  description: "Ask your administrator to set a dispatch phone number",
                });
              }
            }}
            className={`p-2.5 hover:bg-white/5 transition-all border border-transparent hover:border-white/10 ${
              dispatchPhone ? "text-gray-400 hover:text-lime-400" : "text-gray-600"
            }`}
            aria-label="Call dispatch"
            title={dispatchPhone ? "Call Dispatch" : "Dispatch number not configured"}
          >
            <Phone className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    const { latitude, longitude } = position.coords;
                    window.open(
                      `https://www.google.com/maps/@${latitude},${longitude},15z`,
                      "_blank"
                    );
                  },
                  (error) => {
                    if (import.meta.env.DEV) console.error("Geolocation error:", error);
                    toast.error("Location access denied", {
                      description: "Enable location permissions in your browser settings",
                    });
                  }
                );
              } else {
                toast.error("Geolocation not supported", {
                  description: "Your browser doesn't support location services",
                });
              }
            }}
            className="p-2.5 text-gray-400 hover:text-teal-400 hover:bg-white/5 transition-all border border-transparent hover:border-white/10"
            aria-label="Show my location"
            title="My Location"
          >
            <MapPin className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </header>

      {/* Main Content - scrollable */}
      <main id="main-content" className="relative z-10 flex-1 overflow-auto pb-24">
        <PageErrorBoundary>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="p-4 md:p-6"
          >
            <Outlet />
          </motion.div>
        </PageErrorBoundary>
      </main>

      {/* Bottom Navigation - Fixed with premium styling (80px for better touch targets) */}
      <nav aria-label="Bottom navigation" className="fixed bottom-0 left-0 right-0 h-20 bg-[#2d2d2d] border-t border-white/[0.08] flex items-center justify-around px-2 safe-area-pb z-20 shadow-2xl">
        {navItems.map((item, index) => {
          const active = isActive(item.href);
          // Alternate lime/teal for visual variety
          const accentColor = index % 2 === 0 ? "lime" : "teal";

          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex flex-col items-center justify-center py-2 px-3 min-w-[72px] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500/50 focus-visible:rounded ${
                active
                  ? accentColor === "lime"
                    ? "text-lime-400"
                    : "text-teal-400"
                  : "text-gray-500"
              }`}
            >
              <div className="relative">
                <item.icon
                  className={`w-6 h-6 transition-transform ${active ? 'scale-110' : ''}`}
                  strokeWidth={active ? 2.5 : 2}
                />
                {active && (
                  <motion.div
                    layoutId="tech-nav-indicator"
                    className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                      accentColor === "lime" ? "bg-lime-400" : "bg-teal-400"
                    }`}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.3 }}
                  />
                )}
              </div>
              <span className={`text-xs mt-1.5 font-semibold tracking-wide ${active ? '' : 'opacity-70'}`}>
                {item.title}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
